import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import actions from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';

export enum State {
  INIT = 'init',

  FORM_PAGE = 'form-page',
  RECAP = 'recap',
  SEND = 'send',
  TAX_RESIDENCIES = 'tax-residencies',
  SEND_TAX_RESIDENCIES = 'send-tax-residencies',
  US_CITIZENSHIP_NOT_ALLOWED = 'us-citizenship-not-allowed',
  TAX_RESIDENCY_COUNTRY_NOT_ALLOWED = 'tax-residency-country-not-allowed',
  FOREIGN_TAX_RESIDENCY_NOT_ALLOWED = 'foreign-tax-residency-not-allowed',

  DONE = 'done',
  ABORT = 'abort',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'compliance',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.INIT]: {
        always: [
          {
            cond: Guard.CanEditComplianceInformation,
            target: State.FORM_PAGE,
          },
          { target: State.RECAP },
        ],
      },

      [State.FORM_PAGE]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.ABORT,
          SUBMIT_FISCAL_SITUATION: State.SEND,
        },
      },

      [State.RECAP]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.DONE,
          PREV: State.ABORT,
        },
      },

      [State.SEND]: {
        invoke: {
          onDone: [
            {
              cond: Guard.IsSpecifiedUSPerson,
              target: State.US_CITIZENSHIP_NOT_ALLOWED,
            },
            {
              target: State.TAX_RESIDENCIES,
            },
          ],
          onError: {
            actions: shared.actions.error(),
            target: State.FORM_PAGE,
          },
          src: Service.SendFiscalSituation,
        },
      },

      [State.US_CITIZENSHIP_NOT_ALLOWED]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: {
            target: State.FORM_PAGE,
          },
        },
      },

      [State.FOREIGN_TAX_RESIDENCY_NOT_ALLOWED]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: {
            target: State.FORM_PAGE,
          },
        },
      },

      [State.TAX_RESIDENCIES]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: {
            target: State.FORM_PAGE,
          },
          SAVE_TAX_RESIDENCIES: {
            target: State.SEND_TAX_RESIDENCIES,
          },
        },
      },

      [State.SEND_TAX_RESIDENCIES]: {
        invoke: {
          onDone: [
            {
              cond: Guard.IsForeignTaxResidencyNotAllowed,
              target: State.FOREIGN_TAX_RESIDENCY_NOT_ALLOWED,
            },
            {
              cond: Guard.IsNotEligibleToOnboard,
              target: State.TAX_RESIDENCY_COUNTRY_NOT_ALLOWED,
            },
            {
              cond: Guard.IsMissingFiscalId,
              target: State.TAX_RESIDENCIES,
            },
            {
              target: State.DONE,
            },
          ],
          onError: {
            actions: shared.actions.error(),
            target: State.TAX_RESIDENCIES,
          },
          src: Service.SendTaxResidencies,
        },
      },

      [State.TAX_RESIDENCY_COUNTRY_NOT_ALLOWED]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: {
            target: State.TAX_RESIDENCIES,
          },
        },
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
